import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { KitDeletionDialog } from "@/components/dependency-deletion/KitDeletionDialog";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import {
  GroupedTable,
  GroupedTableColumn,
} from "@/components/GroupedTable/GroupedTable";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Column, Row } from "@/components/ui/flex";
import { Input } from "@/components/ui/input";
import { useKitsData } from "@/data/use-kits-data";
import { useUsersData } from "@/data/use-users-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { groupByDate, smartDate } from "@/lib/date";
import { GetKitDto } from "@/lib/gen-api";
import { getInitials } from "@/lib/string";
import { generateDataKey } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

export const RepositoriesPage = () => {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();

  const { kits, createKit, updateKit } = useKitsData(groupUuid, {
    type: "repository",
  });

  const groupedRepositories = useMemo(
    () => groupByDate(kits, "date_updated"),
    [kits],
  );

  const columns: GroupedTableColumn<GetKitDto>[] = [
    { key: "name", label: "Name" },
    { key: "date_updated", label: "Last Modified" },
    { key: "user_uuid", label: "Created By" },
  ];

  async function handleCreateRepository(name: string) {
    if (!name) return;

    const kit = await createKit({
      name,
    });

    if (!kit) {
      toast.error("Failed to create collection");
      return;
    }

    toast.success("Collection created successfully!");
  }

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Shared Repositories</PageHeader.Title>
      </PageHeader>

      <PageBody>
        <Column>
          <p>
            These are the repositories that contain folders shared with other
            groups.
          </p>
        </Column>
        <Row>
          <CreateRepositoryButton onCreateRepository={handleCreateRepository} />
        </Row>
        <GroupedTable
          key={generateDataKey(kits, ["name", "date_updated"])}
          data={groupedRepositories}
          columns={columns}
          itemIdKey="uuid"
          renderCell={{
            name: (_, row) => <KitDisplayName kit={row} />,
            user_uuid: (value) => <UserCell userUuid={value} />,
            date_updated: (value) => smartDate(value),
          }}
          renderActions={({ row }) => (
            <RepositoryRowActions row={row} updateKit={updateKit} />
          )}
          rowLink={(row) => ({
            to: "/w/$groupSlug/k/$kitUuid",
            params: { groupSlug, kitUuid: row.uuid },
          })}
          emptyMessage="No collections here yet. Create your first to organize your team's shared knowledge for your Expert AIs."
        />
      </PageBody>
    </Page>
  );
};

function UserCell({ userUuid }: { userUuid: string }) {
  const groupUuid = useGroupUuid();
  const { getUserForUserUuid } = useUsersData(groupUuid);
  const user = getUserForUserUuid(userUuid);
  if (user === undefined) {
    return <span>Unknown User</span>;
  }
  return (
    <Row className="items-center gap-2">
      <Avatar className="h-5 w-5">
        <AvatarFallback>{getInitials(user.name || "", 2)}</AvatarFallback>
        <AvatarImage src={user.photo_url ?? undefined} />
      </Avatar>
      <span>{user.name}</span>
    </Row>
  );
}

function RepositoryRowActions({
  row,
  updateKit,
}: {
  row: GetKitDto;
  updateKit: (params: { kitUuid: string; name: string }) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState(row.name);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNewName(row.name);
  }, [row.name]);

  const handleEdit = async () => {
    setLoading(true);
    setError(null);

    try {
      await updateKit({ kitUuid: row.uuid, name: newName });
      setOpen(false);
      toast.success("Collection name updated");
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setError("Failed to update name");
      toast.error("Failed to update name");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Collection Name</DialogTitle>
            <DialogDescription>
              Change the name of this collection. This will be visible to all
              group members.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Collection name"
            disabled={loading}
            className="mb-2"
            autoFocus
          />
          {error && <div className="mb-2 text-sm text-red-500">{error}</div>}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleEdit}
              disabled={loading || !newName.trim()}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <KitDeletionDialog groupUuid={row.group_uuid} kitUuid={row.uuid}>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-destructive hover:text-destructive-foreground"
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </KitDeletionDialog>
    </>
  );
}

function CreateRepositoryButton({
  onCreateRepository,
}: {
  onCreateRepository: (name: string) => void;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [repositoryName, setRepositoryName] = useState("");

  function handleCreateRepository() {
    if (repositoryName.trim()) {
      onCreateRepository(repositoryName.trim());
      setRepositoryName("");
      setIsDialogOpen(false);
    }
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button>Create New Collection</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Collection</DialogTitle>
          <DialogDescription>
            Enter a name for your new collection.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            value={repositoryName}
            onChange={(e) => setRepositoryName(e.target.value)}
            placeholder="Name"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleCreateRepository();
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsDialogOpen(false)}
            size="sm"
          >
            Cancel
          </Button>
          <Button type="submit" onClick={handleCreateRepository} size="sm">
            Create Collection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
